import React, { useState, useContext, useEffect } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import { useRecoilState, useRecoilValue } from 'recoil';
import { v4 as uuidv4 } from 'uuid';
import { getQueryStringParameter, createComponentMapping, deepFormFieldSearch, isDebugging, isObjectEmpty } from '../../utils.js';
import sanitizeHtml from 'sanitize-html';
import { Layout } from '../Layout';
import { FormContainer } from '../../components/Form';
import { Modal, ModalText, ModalButton } from '../../components/Modal';
import { ShareDraft } from '../../components/ShareDraft';
import { LoadingIcon } from '../../components/LoadingIcon';
import { Debug } from '../../components/Debug';
import { SettingsContext } from '../../components/SettingsContext';
import { FhirClientContext } from '../../components/FhirClientContext';
import { CssLoader } from '../../components/CssLoader';
import { useFormPrefs, useFormControls, useFhirResolver, useFhirResources } from '../../hooks';
import {
	formSchemaAtom,
	formDefaultAtom,
	submissionAtom,
	submissionGuidAtom,
	correlationGuidAtom
} from '../../recoil/form';

const Home = () => {
	const settings = useContext(SettingsContext);
	const fhirClient = useContext(FhirClientContext);
	const [submissionGuidState, setSubmissionGuidState] = useRecoilState(submissionGuidAtom);
	const [correlationGuidState, setCorrelationGuidState] = useRecoilState(correlationGuidAtom);
	const [submissionState, setSubmissionState] = useRecoilState(submissionAtom);
	const [formDefaultState, setFormDefaultState] = useRecoilState(formDefaultAtom);
	const [formSchemaState, setFormSchemaState] = useRecoilState(formSchemaAtom);
	const [fhirFormUrl, setFhirFormUrl] = useState('');
	const [formCssUrl, setFormCssUrl] = useState('');
	const [formErrors, setFormErrors] = useState([]);
	const [apiResponse, setApiResponse] = useState('');
	const [modalMessage, setModalMessage] = useState('');
	const [isLoading, setIsLoading] = useState(false);
	const [isModalActive, setIsModalActive] = useState(false);
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [isSubmitted, setIsSubmitted] = useState(false);
	const { formpath, environmentpath } = useParams();
	const location = useLocation();

	const fhirEndpoint = settings.fhirEndpoint;

	const { fhirResources, isFhirResourcesLoaded } = useFhirResources(fhirClient, settings.fhirPathResources);
	const { fhirResolve, fhirResolveString } = useFhirResolver(
		() => (fhirResources || []).concat({ ...submissionState.data }), [fhirResources, submissionState]);

	const formFeatures = useFormPrefs(settings, fhirClient, fhirResolve('Patient.id'), formSchemaState?._id);
	const formControls = useFormControls(settings, fhirClient, fhirEndpoint, fhirFormUrl, fhirResolve);
	

	useEffect(() => {
		outputStatusMessages();
	}, []);

	useEffect(() => {
		if (!isObjectEmpty(formSchemaState) && isFhirResourcesLoaded) {
			setSubmissionGuidState(uuidv4());

			let qs = getQueryStringParameter(location.search, 'submission');

			if (qs) {
				setSubmissionGuidState(qs);
				formControls.getDraft(qs).then((data) => {
					if (data)
						setFormDefaultState(data);
				});
			} else {
				const formSchema = JSON.parse(JSON.stringify(formSchemaState));
				const mapping = createComponentMapping(formSchema);
				let submissionObj =
					createSubmissionObj(mapping, formSchemaState);

				outputFormErrors();
				setFormDefaultState({ data: submissionObj });
			}
		}
	}, [formSchemaState, isFhirResourcesLoaded]);

	useEffect(() => {
		setSubmissionState({});
		setFormDefaultState({});

		let formUrl = formpath ? `${fhirEndpoint}/formio/formtemplate?env=${environmentpath}&id=${formpath}` : '';
		let cssUrl = formpath ? `${fhirEndpoint}/form/${formpath}/css` : '';

		setFhirFormUrl(formUrl);
		setFormCssUrl(cssUrl);
	}, [formpath, environmentpath]);

	useEffect(() => {
		if (fhirFormUrl) {
			formControls.getFormSchema();
		} else {
			setFormSchemaState({});
			setFormDefaultState({});
			setSubmissionGuidState('');
		}

		setFormErrors([]);
		setCorrelationGuidState(uuidv4());
	}, [fhirFormUrl]);

	const createSubmissionObj = (mapping, formJson) => {
		let submissionObj = {};

		for (var key in mapping) {
			let path = mapping[key];
			let match = key.match(/(\w+)(\[(\d*)\]\.(\w+))?/i); // 1: parent datagrid, 3: index, 4: child component

			try {
				let parentKey = match[1];
				let arrayIdx = match[3] ? parseInt(match[3], 10) : -1;
				let propertyKey = match[4];
				let value = fhirResolve(path, true);

				if (value === null) continue;

				if (propertyKey) {
					let field = deepFormFieldSearch(formJson, 'key', parentKey);
					// if arrayIdx < 0, map to all rows; otherwise to one specific row
					let min = Math.max(arrayIdx, 0); 
					let max = arrayIdx < 0 ? (field.defaultValue ? field.defaultValue.length : 1) : arrayIdx + 1;
					submissionObj[parentKey] = submissionObj[parentKey] || field.defaultValue;

					for (let i = min; i < max; i++) {
						submissionObj[parentKey][i][propertyKey] = value;
					}
				} else {
					submissionObj[key] = value;
				}
			} catch {
				console.error(`[eForm] Error mapping values with fhirpath ${path} for field "${key}"`);
			}
		}
		return submissionObj;
	}

	const outputFormErrors = () => {
		const sortedArray = [].concat(formErrors).sort();
		let previousItem = '';
		let lastOutputted = '';

		for (let x = 0; x < sortedArray.length; x++) {
			if (previousItem === sortedArray[x] && sortedArray[x] !== lastOutputted) {
				console.log(sortedArray[x]);
				lastOutputted = sortedArray[x];
			}
			previousItem = sortedArray[x];
		}
	}

	const submitForm = (submission) => {
		if (!isSubmitting) {
			// set state to prevent additional submissions
			setIsSubmitting(true);
			setIsLoading(true);

			formControls.submit(submission).then((data) => {
				setModalMessage(data.message);
				setApiResponse(getResponseMessage(data));

				if (data.isSubmitted) {
					setIsSubmitted(true);
				}

				// remove loading image
				setIsLoading(false);
				setIsModalActive(true);

				// allow submission button to be clicked
				setIsSubmitting(false);
			});
		}
	}

	const savePdf = (isDraft = 0) => {
		setIsLoading(true);

		formControls.savePdf(isDraft).then((data) => {
			setModalMessage(data.message);
			setApiResponse(getResponseMessage(data));
			setIsLoading(false);
			setIsModalActive(true);
		});
	}

	const getPdf = () => {
		setTimeout(() => {
			setApiResponse('PDF created successfully, download will start automatically. If download does not start, please ensure you have enabled pop-ups for <a href="https://www.eforms.healthbc.org/">https://www.eforms.healthbc.org/</a>');
			setModalMessage('');
			setIsLoading(false);
			setIsModalActive(true);
			formControls.getPdf();
		}, 2000);
	}

	const saveDraft = (enableFeedback = false, isDraftButton = false) => {
		if (enableFeedback) {
			setIsLoading(true);
		}

		formControls.saveDraft(isDraftButton).then((data) => {
			if (enableFeedback) {
				setModalMessage(data.message);
				setApiResponse(getResponseMessage(data));
				setIsLoading(false);
				setIsModalActive(true);
			}
		});
	}

	const closeModal = () => {
		setIsModalActive(!isModalActive);
		setIsSubmitted(false);
		setApiResponse('');
		setModalMessage('');

		if (isSubmitted) {
			// reset form on successful submission
			setFormDefaultState({ data: formDefaultState.data });
			setSubmissionState({});

			if (settings.submissionRedirectUrl) {
				window.location = formFeatures.submissionRedirectUrl || settings.submissionRedirectUrl;
			}
		}
	}
	 
	const updateForm = (updatedData) => {
		if (updatedData) {
			setSubmissionState(JSON.parse(JSON.stringify(updatedData)));
		}
	}

	const getResponseMessage = (data) => {
		return data.response;
	}

	const outputStatusMessages = () => {
		if (formFeatures.enableLauncherOnly) {
			console.log('Launcher only mode is enabled.');
		}
	}

	const wrapperClassname = isLoading ? 'form-submitting' : '';
	const formBaseUrl = `${fhirEndpoint}/project/${environmentpath}`;

	return (
		<Layout>
			{
				formCssUrl ?
					<CssLoader
						cssLink={formCssUrl}
					/>
					: null
			}
			{
				formFeatures.enableLauncherOnly && !fhirClient.client ?
					null
					:
					<>
						<div id="form-container" className={wrapperClassname}>
							<FormContainer
								formJson={formSchemaState}
								formTitle={formSchemaState.title}
								submission={formDefaultState}
								submissionWarning={fhirResolveString(formFeatures.preSubmissionMessage)}
								onChange={updateForm}
								onSubmit={submitForm}
								baseUrl={formBaseUrl}
							/>
						</div>
						
						{
							(JSON.stringify(formSchemaState) !== JSON.stringify({})) ?
								<div className="form-actions text-center">
									{
										(isDebugging() || formFeatures.enableDraftSharing) &&
										formFeatures.enableDraft &&
										fhirClient.client &&

										<ShareDraft
											formName={formSchemaState.title}
											patientName={fhirResolveString(formFeatures.settingPatientName)}
											saveDraft={formControls.saveDraft}
										/>
									}
									{
										formFeatures.enableDraft &&
										fhirClient.client &&

										<button onClick={() => { saveDraft(true, true) }} className="btn btn-secondary">Save Draft</button>
									}
									{
										formFeatures.enableDownloadDraftPdf ?
											<button onClick={() => { savePdf(1) }} className="btn btn-secondary">Download Draft PDF</button>
											: null
									}
								</div>
								: null
						}

						<LoadingIcon isLoading={isLoading} />

						{
							isModalActive &&
								<Modal>
									<ModalText
										message={<div dangerouslySetInnerHTML={{ __html: sanitizeHtml(apiResponse) }}></div>}
									/>
									<ModalText
										message={modalMessage}
									/>

									{
									isSubmitted && formFeatures.enableDownloadPdf &&
										<ModalButton
											label="Download PDF"
											handleButtonClick={() => { getPdf() }}
										/>
									}

									<ModalButton
										label="OK"
										handleButtonClick={closeModal}
									/>
								</Modal>
						}
					</>
			}

			{
				formFeatures.enableDebugger && document.cookie.indexOf('debugger') >= 0 ?
					<Debug
						client={fhirClient.client}
						fhirResolve={fhirResolve}
						form={formSchemaState}
					/>
					: null
			}
		</Layout>
	);
}

export default Home;